import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { savedCircuitConfigurationKey, type SavedCircuitLapSet } from '@kangaroo/shared';
import { isAxiosError } from 'axios';

import { updateTelemetrySessionContext } from '../features/telemetryContext';
import { savedCircuitLapSetForOverlay, type CircuitMapOverlay } from '../lib/circuitMapOverlay';
import {
  buildCircuitLapReslice,
  buildReslicedRankPool,
  buildSavedCircuitLapSet,
  buildSessionLapTimingOverlay,
  bindCircuitResliceToConfiguration,
  circuitResliceForConfiguration,
  defaultReslicedFlyingLapNumber,
  resliceFromSavedCircuitLapSet,
  reslicedFlyingLaps,
  selectedReslicedLapWindow,
  type ConfigurationScopedCircuitReslice,
} from '../lib/circuitLapReslice';
import type { MyChronSessionExport } from '../lib/telemetrySession';

function formatPersistError(error: unknown, fallback: string) {
  if (isAxiosError(error)) {
    const message = error.response?.data?.message;
    if (typeof message === 'string' && message.trim()) {
      return message;
    }
  }

  if (error instanceof Error && error.message.trim()) {
    return error.message;
  }

  return fallback;
}

type UseCircuitLapResliceOptions = {
  session: MyChronSessionExport | null;
  sessionKey: string;
  circuitOverlay: CircuitMapOverlay | null;
  savedCircuitLapSet: SavedCircuitLapSet | null | undefined;
  canPersist: boolean;
  onSaved?: () => void | Promise<void>;
  skipInitialReslice?: boolean;
};

export function useCircuitLapReslice({
  session,
  sessionKey,
  circuitOverlay,
  savedCircuitLapSet,
  canPersist,
  onSaved,
  skipInitialReslice = false,
}: UseCircuitLapResliceOptions) {
  const [resliceGeneration, setResliceGeneration] = useState(0);
  const [draftReslice, setDraftReslice] = useState<ConfigurationScopedCircuitReslice | null>(null);
  const [selectedLapNumber, setSelectedLapNumber] = useState(1);
  const [useFixedLaps, setUseFixedLaps] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const preferFlyingLapRef = useRef(true);
  const fixedLapPreferenceInitializedRef = useRef(false);

  const coherentSavedCircuitLapSet = useMemo(
    () => savedCircuitLapSetForOverlay(savedCircuitLapSet, circuitOverlay),
    [circuitOverlay, savedCircuitLapSet],
  );
  const savedReslice = useMemo(
    () =>
      coherentSavedCircuitLapSet ? resliceFromSavedCircuitLapSet(coherentSavedCircuitLapSet) : null,
    [coherentSavedCircuitLapSet],
  );
  const configurationKey = savedCircuitConfigurationKey(circuitOverlay?.configuration);
  const activeDraftReslice = useMemo(
    () => circuitResliceForConfiguration(draftReslice, circuitOverlay?.configuration),
    [circuitOverlay?.configuration, draftReslice],
  );

  const activeReslice = activeDraftReslice ?? savedReslice;

  const regenerate = useCallback(() => {
    if (!session || !circuitOverlay?.startFinishLine || !configurationKey) {
      return;
    }

    const next = buildCircuitLapReslice(session, circuitOverlay);
    setDraftReslice(bindCircuitResliceToConfiguration(next, circuitOverlay.configuration));
    preferFlyingLapRef.current = true;
    setResliceGeneration((value) => value + 1);
    setUseFixedLaps(next.laps.length > 0);
  }, [circuitOverlay, configurationKey, session]);

  useEffect(() => {
    fixedLapPreferenceInitializedRef.current = false;
    preferFlyingLapRef.current = true;
  }, [configurationKey, sessionKey]);

  useEffect(() => {
    if (!session || !circuitOverlay?.startFinishLine || !configurationKey) {
      setDraftReslice(null);
      if (!fixedLapPreferenceInitializedRef.current) {
        setUseFixedLaps(false);
        fixedLapPreferenceInitializedRef.current = true;
      }
      return;
    }

    if (savedReslice?.laps.length) {
      if (!fixedLapPreferenceInitializedRef.current) {
        setUseFixedLaps(true);
        fixedLapPreferenceInitializedRef.current = true;
      }
      return;
    }

    if (!fixedLapPreferenceInitializedRef.current) {
      if (!skipInitialReslice) {
        const initial = buildCircuitLapReslice(session, circuitOverlay);
        if (initial.laps.length > 0) {
          setDraftReslice(bindCircuitResliceToConfiguration(initial, circuitOverlay.configuration));
          setUseFixedLaps(true);
        }
      }
      fixedLapPreferenceInitializedRef.current = true;
    }
  }, [
    circuitOverlay,
    configurationKey,
    resliceGeneration,
    savedReslice,
    session,
    sessionKey,
    skipInitialReslice,
  ]);

  useEffect(() => {
    if (!preferFlyingLapRef.current || !activeReslice?.laps.length) {
      return;
    }

    setSelectedLapNumber(defaultReslicedFlyingLapNumber(activeReslice));
    preferFlyingLapRef.current = false;
  }, [activeReslice]);

  useEffect(() => {
    if (!activeReslice?.laps.length) {
      return;
    }

    if (!activeReslice.laps.some((lap) => lap.lapNumber === selectedLapNumber)) {
      setSelectedLapNumber(defaultReslicedFlyingLapNumber(activeReslice));
    }
  }, [activeReslice, selectedLapNumber]);

  const selectedLap = useMemo(
    () => (activeReslice ? selectedReslicedLapWindow(activeReslice, selectedLapNumber) : null),
    [activeReslice, selectedLapNumber],
  );

  const rankPool = useMemo(
    () => (activeReslice ? buildReslicedRankPool(activeReslice.laps) : []),
    [activeReslice],
  );

  const flyingLaps = useMemo(
    () => (activeReslice ? reslicedFlyingLaps(activeReslice) : []),
    [activeReslice],
  );

  const saveFixedLaps = useCallback(async () => {
    if (!canPersist || !circuitOverlay || !activeReslice?.laps.length) {
      return;
    }

    try {
      setSaving(true);
      setSaveError(null);
      const circuitLapSet = buildSavedCircuitLapSet(circuitOverlay, activeReslice);
      if (circuitLapSet.laps.length === 0) {
        throw new Error('No valid laps to save. Try regenerating first.');
      }
      const lapTimingOverlay = session
        ? buildSessionLapTimingOverlay(session, circuitOverlay, activeReslice, circuitLapSet)
        : null;

      await updateTelemetrySessionContext(sessionKey, {
        circuitLapSet,
        lapTimingOverlay,
        contributionId: circuitOverlay.contributionId,
        trackId: circuitOverlay.trackId,
      });
      setDraftReslice(null);
      setUseFixedLaps(true);
      await onSaved?.();
    } catch (error) {
      setSaveError(formatPersistError(error, 'Could not save fixed laps.'));
    } finally {
      setSaving(false);
    }
  }, [activeReslice, canPersist, circuitOverlay, onSaved, session, sessionKey]);

  const clearSavedFixedLaps = useCallback(async () => {
    if (!canPersist) {
      return;
    }

    try {
      setSaving(true);
      setSaveError(null);
      await updateTelemetrySessionContext(sessionKey, {
        circuitLapSet: null,
        lapTimingOverlay: null,
      });
      setDraftReslice(null);
      setUseFixedLaps(false);
      await onSaved?.();
    } catch (error) {
      setSaveError(formatPersistError(error, 'Could not clear fixed laps.'));
    } finally {
      setSaving(false);
    }
  }, [canPersist, onSaved, sessionKey]);

  return {
    enabled: Boolean(
      session &&
      ((circuitOverlay?.startFinishLine && configurationKey) ||
        (coherentSavedCircuitLapSet?.laps.length ?? 0) > 0),
    ),
    activeReslice,
    savedReslice,
    hasSaved: Boolean(coherentSavedCircuitLapSet),
    hasDraft: Boolean(activeDraftReslice),
    selectedLapNumber,
    setSelectedLapNumber: (lapNumber: number) => {
      preferFlyingLapRef.current = false;
      setSelectedLapNumber(lapNumber);
    },
    selectedLap,
    rankPool,
    flyingLaps,
    useFixedLaps: useFixedLaps && Boolean(activeReslice?.laps.length),
    setUseFixedLaps,
    regenerate,
    saveFixedLaps,
    clearSavedFixedLaps,
    saving,
    saveError,
    savedAt: coherentSavedCircuitLapSet?.savedAt ?? null,
  };
}
